import { computed, DestroyRef, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { tap } from 'rxjs';
import { TokenClaims } from '@ceres/interfaces';
import { ENVIRONMENT } from '@ceres/frontend-helper';
import { AppUserService, User, UserService } from '@ceres/shared/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private msal = inject(MsalService);
    private msalBroadcast = inject(MsalBroadcastService);
    private userService = inject(UserService);
    private appUserService = inject(AppUserService);
    private environment = inject(ENVIRONMENT);
    private destroyRef = inject(DestroyRef);

    private _isAuthenticated: WritableSignal<boolean> = signal(false);
    private _isAuthLoading: WritableSignal<boolean> = signal(true);
    private _userAccount: WritableSignal<AccountInfo | null> = signal(null);

    public isAuthenticated: Signal<boolean> = computed(() => this._isAuthenticated());
    public isAuthLoading: Signal<boolean> = computed(() => this._isAuthLoading());
    public userIdTokenClaims: Signal<TokenClaims | null> = computed(
        () => this._userAccount()?.idTokenClaims as TokenClaims
    );

    public userName: Signal<string | null> = computed(() => {
        const claims = this.userIdTokenClaims();
        if (!claims) return null;

        return `${claims.preferred_family_name}, ${claims.preferred_given_name}`;
    });

    constructor() {
        this.initializeAuth();
    }

    private initializeAuth(): void {
        // only used for loader
        this.msalBroadcast.inProgress$
            .pipe(
                tap((status) => {
                    this._isAuthLoading.set(status !== InteractionStatus.None);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // real login logic based on MSAL events (no races)
        this.msalBroadcast.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    switch (msg.eventType) {
                        case EventType.LOGIN_SUCCESS: {
                            const authResult: any = msg.payload;
                            const account = authResult?.account as AccountInfo;

                            if (account) {
                                this.msal.instance.setActiveAccount(account);
                                this._userAccount.set(account);
                                this._isAuthenticated.set(true);
                                this.loadUserFromBackend(account);
                            }
                            break;
                        }

                        case EventType.ACQUIRE_TOKEN_SUCCESS: {
                            // Only update account info, don't reload user from backend
                            // to avoid infinite loops on token refresh
                            const authResult: any = msg.payload;
                            const account = authResult?.account as AccountInfo;

                            if (account && !this._isAuthenticated()) {
                                this.msal.instance.setActiveAccount(account);
                                this._userAccount.set(account);
                                this._isAuthenticated.set(true);
                            }
                            break;
                        }

                        case EventType.LOGOUT_SUCCESS:
                            this._isAuthenticated.set(false);
                            this._userAccount.set(null);
                            break;
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // hydrate from cache at startup (once)
        this.hydrateFromCache();
    }

    private hydrateFromCache() {
        const accs = this.msal.instance.getAllAccounts();
        if (accs.length > 0) {
            const acc = this.msal.instance.getActiveAccount() || accs[0];
            this.msal.instance.setActiveAccount(acc);
            this._userAccount.set(acc);
            this._isAuthenticated.set(true);
            this.loadUserFromBackend(acc);
        } else {
            this._isAuthenticated.set(false);
        }
    }

    private loadUserFromBackend(account: AccountInfo): void {
        try {
            const gid = account.idTokenClaims?.['gid'] as string;
            if (!gid) throw new Error('GID missing in token');

            this.userService
                .getUserObservable(gid)
                .pipe(
                    tap((u: User) => this.loginSuccess(u)),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();
        } catch (err) {
            console.error('[Auth] setSessionFromMsal failed', err);
            this._isAuthenticated.set(false);
        }
    }

    private loginSuccess(user: User): void {
        this.appUserService.setMainUser(user);
    }

    public logout(): void {
        this.msal.logoutRedirect();
    }

    public login(): void {
        // do *not* trigger login while MSAL is already in progress
        const accs = this.msal.instance.getAllAccounts();
        if (accs.length > 0) return;

        this.msal.loginRedirect();
    }
}
